<template>
  <v-card>
    <v-toolbar color="indigo" elevation="4">
      <v-toolbar-title class="toolbar-title">Copilot Metrics</v-toolbar-title>
      <h2 class="error-message"> {{ mockedDataMessage }} </h2>
      <v-spacer></v-spacer>

      <!-- Modified toggle switch with better visibility -->
      <v-switch
        v-model="showTeamMetrics"
        :label="showTeamMetrics ? 'Team Metrics' : 'Org Metrics'"
        class="metrics-toggle"
        density="compact"
        color="white"
        track-color="grey-darken-3"
        hide-details
        @update:model-value="handleMetricsToggle"
      ></v-switch>

      <!-- Add team selector dropdown -->
      <v-select
        v-if="showTeamMetrics"
        v-model="selectedTeam"
        :items="teamsList"
        item-title="name"
        item-value="name"
        label="Select Team"
        density="compact"
        class="team-select"
        hide-details
        style="max-width: 200px;"
        @update:model-value="handleTeamChange"
      ></v-select>

      <v-btn v-if="showLogoutButton" href="/logout" class="logout-button">Logout</v-btn>

      <template v-slot:extension>

        <v-tabs v-model="tab" align-tabs="title">
          <v-tab v-for="item in tabItems" :key="item" :value="item">
            {{ item }}
          </v-tab>
        </v-tabs>

      </template>

    </v-toolbar>

    <!-- Add data not found message -->
    <div v-if="dataNotFoundMessage" class="data-not-found-message">
      {{ dataNotFoundMessage }}
    </div>

    <!-- API Error Message -->
    <div v-if="apiError && !signInRequired" class="error-message" v-html="apiError"></div>
    <div v-if="signInRequired" class="github-login-container">
      <a href="/login" class="github-login-button">
        <v-icon left>mdi-github</v-icon>
        Sign in with GitHub
      </a>
    </div>
    <div v-if="!apiError">
      <v-progress-linear v-if="!metricsReady" indeterminate color="indigo"></v-progress-linear>
      <v-window v-if="metricsReady" v-model="tab">
        <v-window-item v-for="item in tabItems" :key="item" :value="item">
          <v-card flat>
            <MetricsViewer v-if="item === itemName" :metrics="metrics" />
            <BreakdownComponent v-if="item === 'languages'" :metrics="metrics" :breakdownKey="'language'" />
            <BreakdownComponent v-if="item === 'editors'" :metrics="metrics" :breakdownKey="'editor'" />
            <CopilotChatViewer v-if="item === 'copilot chat'" :metrics="metrics" />
            <SeatsAnalysisViewer v-if="item === 'seat analysis'" :seats="seats" />
            <ApiResponse v-if="item === 'api response'" :metrics="metrics" :seats="seats" />
          </v-card>
        </v-window-item>
      </v-window>
    </div>

  </v-card>
</template>

<script lang='ts'>
import { defineComponent, ref } from 'vue'
import { getMetricsApi, getTeamMetricsApi, getTeams } from '../api/GitHubApi';
import { getSeatsApi } from '../api/ExtractSeats';
import { Metrics } from '../model/Metrics';
import { Seat } from "../model/Seat";
import config from '../config';
import { AxiosError } from 'axios';

//Components
import MetricsViewer from './MetricsViewer.vue'
import BreakdownComponent from './BreakdownComponent.vue' 
import CopilotChatViewer from './CopilotChatViewer.vue' 
import SeatsAnalysisViewer from './SeatsAnalysisViewer.vue'
import ApiResponse from './ApiResponse.vue'

export default defineComponent({
  name: 'MainComponent',
  components: {
    MetricsViewer,
    BreakdownComponent,
    CopilotChatViewer,
    SeatsAnalysisViewer,
    ApiResponse
  },
  setup() {
    const metricsReady = ref(false);
    const metrics = ref<Metrics[]>([]);
    const seatsReady = ref(false);
    const seats = ref<Seat[]>([]);
    const apiError = ref<string | undefined>(undefined);
    const signInRequired = ref(false);
    const showTeamMetrics = ref(false);
    const tab = ref(null);
    const tabItems = ref(['languages', 'editors', 'copilot chat', 'seat analysis', 'api response']);
    const teamsList = ref<any[]>([]);
    const selectedTeam = ref<string | null>(null);
    const dataNotFoundMessage = ref('');

    // Make config available in template
    const githubConfig = config;

    // Function to fetch teams
    const fetchTeams = async () => {
      try {
        const teams = await getTeams();
        teamsList.value = teams;
      } catch (error) {
        processError(error);
      }
    };

    const handleTeamChange = async () => {
      if (selectedTeam.value) {
        metricsReady.value = false;
        dataNotFoundMessage.value = '';
        try {
          console.log('Fetching metrics for team:', selectedTeam.value);
          config.github.team = selectedTeam.value || '';
          const data = await getTeamMetricsApi();
          console.log('Team metrics received:', data);
          metrics.value = data;
          metricsReady.value = true;
        } catch (error) {
          console.error('Error fetching team metrics:', error);
          if ((error as AxiosError).response?.status === 404) {
            metrics.value = [];
            dataNotFoundMessage.value = `No data found for team: ${selectedTeam.value}`;
            metricsReady.value = true;
          } else {
            processError(error);
          }
        }
      }
    };

    const handleMetricsToggle = async () => {
      metricsReady.value = false;
      dataNotFoundMessage.value = '';
      
      try {
        if (showTeamMetrics.value) {
          console.log('Fetching teams list');
          await fetchTeams();
          if (teamsList.value.length > 0) {
            const teamName = teamsList.value[0].name;
            selectedTeam.value = teamName;
            console.log('Selected first team:', teamName);
            config.github.team = teamName;
            try {
              const data = await getTeamMetricsApi();
              console.log('Initial team metrics received:', data);
              metrics.value = data;
            } catch (error) {
              if ((error as AxiosError).response?.status === 404) {
                metrics.value = [];
                dataNotFoundMessage.value = `No data found for team: ${teamName}`;
              } else {
                throw error;
              }
            }
          }
        } else {
          selectedTeam.value = null;
          config.github.team = '';
          console.log('Fetching org metrics');
          const data = await getMetricsApi();
          metrics.value = data;
        }
        metricsReady.value = true;
      } catch (error) {
        console.error('Error in metrics toggle:', error);
        processError(error);
      }
    };

    const processError = (error: any) => {
      console.log(error);
      if (error.response && error.response.status) {
        switch (error.response.status) {
          case 401:
            apiError.value = '401 Unauthorized access - check if your token in the .env file is correct.';
            if (config.github.baseApi === '/api/github') {
              signInRequired.value = true;
            }
            break;
          case 404:
            apiError.value = `404 Not Found - is the ${config.scope.type} '${config.scope.name}' correct?`;
            apiError.value = error.message;
        }
        apiError.value += ' <br> If .env file is modified, restart the app for the changes to take effect.';
      }
    };

    // Initial data fetch - Modified to always fetch initial data
    getMetricsApi().then(data => {
      metrics.value = data;
      metricsReady.value = true;
    }).catch(processError);

    getSeatsApi().then(data => {
      seats.value = data;
      seatsReady.value = true;
    }).catch(processError);

    return {
      metricsReady,
      metrics,
      seatsReady,
      seats,
      apiError,
      signInRequired,
      showTeamMetrics,
      handleMetricsToggle,
      processError,
      tab,
      tabItems,
      config: githubConfig,
      teamsList,
      selectedTeam,
      handleTeamChange,
      dataNotFoundMessage,
    };
  },
  computed: {
    gitHubOrgName() {
      return config.github.org;
    },
    itemName() {
      return config.scope.type;
    },
    capitalizedItemName(): string {
      return this.itemName.charAt(0).toUpperCase() + this.itemName.slice(1);
    },
    displayedViewName(): string {
      return config.scope.name;
    },
    isScopeOrganization() {
      return config.scope.type === 'organization';
    },
    teamName() {
      var teamName;
      if (config.github.team && config.github.team.trim() !== '') {
        teamName = "| Team : " + config.github.team;
      } else {
        teamName = '';
      }
      return teamName;
    },
    mockedDataMessage() {
      return config.mockedData ? 'Using mock data - see README if unintended' : '';
    },
    showLogoutButton() {
      return config.github.baseApi === '/api/github';
    }
  },
  created() {
    this.tabItems.unshift(this.itemName);
  }
});
</script>

<style scoped>
.toolbar-title {
  white-space: nowrap;
  overflow: visible;
  text-overflow: clip;

}
.error-message {
  color: red;
}
.logout-button {
  margin-left: auto;
}
.github-login-container {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}
.github-login-button {
  display: flex;
  align-items: center;
  background-color: #24292e;
  color: white;
  padding: 10px 20px;
  border-radius: 5px;
  text-decoration: none;
  font-weight: bold;
  font-size: 14px;
}
.github-login-button:hover {
  background-color: #444d56;
}
.github-login-button v-icon {
  margin-right: 8px;
}
.metrics-toggle {
  margin-right: 16px;
}
.team-select {
  width: 200px !important;
  min-width: 200px !important;
  max-width: 200px !important;
  margin-left: 16px;
  margin-right: 16px;
}
.data-not-found-message {
  color: #666;
  text-align: center;
  padding: 8px;
  background-color: #f5f5f5;
  margin: 8px;
  border-radius: 4px;
}
</style>